/**
 * File: businessMergeUtils.ts
 * Path: packages/config/src/businesses/businessMergeUtils.ts
 * Description: Pure rules for joining two duplicate business records into one
 * (data tidy, Liam 3 Nov 2026): which record is kept, which blanks it fills,
 * which old ids it remembers, and the shapes the merge callable returns.
 * Nothing here reads or writes the database.
 */

export type BusinessRow = Record<string, unknown> & { id: string };

const asObject = (value: unknown): Record<string, unknown> =>
  typeof value === "object" && value !== null ? (value as Record<string, unknown>) : {};

const idText = (value: unknown): string => (typeof value === "string" ? value : "");

/** Every old id a business stands for, plus the businesses merged into it. */
export const legacyIdsOf = (row: BusinessRow): string[] => {
  const legacy = asObject(row.legacy);
  const merged = Array.isArray(legacy.mergedFrom) ? legacy.mergedFrom : [];
  return [row.id, idText(legacy.leadId), idText(legacy.clientId), ...merged.map(idText)].filter(Boolean);
};

const createdMillis = (row: BusinessRow): number => {
  const value = row.createdAt;
  if (typeof value === "number") return value;
  if (typeof value === "object" && value !== null && "toMillis" in value) {
    return (value as { toMillis: () => number }).toMillis();
  }
  return Number.POSITIVE_INFINITY;
};

/** The older record is kept (a tie goes to the smaller id); the other is joined into it. */
export const planMerge = (a: BusinessRow, b: BusinessRow): { keep: BusinessRow; drop: BusinessRow } => {
  const aOlder = createdMillis(a) !== createdMillis(b) ? createdMillis(a) < createdMillis(b) : a.id < b.id;
  return aOlder ? { keep: a, drop: b } : { keep: b, drop: a };
};

const isBlank = (v: unknown): boolean =>
  v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);

/** Fields the kept record never takes from the joined one (numbering is handled on its own). */
const NEVER_COPIED = ["id", "legacy", "folder", "stage", "createdAt", "updatedAt", "notDuplicateOf", "clientNumber", "contractCount", "lastContractId", "migratedAt"];

/** Fields the kept record lacks, taken from the one joined into it. System fields are never copied. */
export const fillBlanksFrom = (keep: BusinessRow, drop: BusinessRow): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(drop)) {
    if (NEVER_COPIED.includes(key)) continue;
    if (isBlank(keep[key]) && !isBlank(value)) out[key] = value;
  }
  return out;
};

/** Ids the kept record remembers: the joined one's id and its own old ids. */
export const mergedFromAfter = (keep: BusinessRow, drop: BusinessRow): string[] => {
  const existing = asObject(keep.legacy).mergedFrom;
  const before = Array.isArray(existing) ? existing.map(idText).filter(Boolean) : [];
  return [...new Set([...before, ...legacyIdsOf(drop)])];
};

/** Ids of entries copied under the kept record, so a second run overwrites rather than doubles. */
export const copiedEntryId = (dropId: string, entryId: string): string => `m-${dropId}-${entryId}`;

/** True when the record is a numbered client (its contracts carry that number). */
export const isNumberedClient = (row: BusinessRow): boolean =>
  typeof row.clientNumber === "number" && Number.isSafeInteger(row.clientNumber) && row.clientNumber >= 1;

/** One line of the merge preview: what will move, in plain words. */
export interface BusinessMergeLine {
  label: string;
  count: number;
}

export type BusinessMergeResult =
  | {
      applied: boolean;
      keepId: string;
      dropId: string;
      lines: BusinessMergeLine[];
    }
  | { error: "not-found" | "same-record" | "both-numbered"; message: string };
